/**
 * LinksPage.jsx — Link tracking & redirect management
 *
 * Admin-only page for creating trackable short links with:
 * - Link list with click counts and QR codes
 * - Create/edit dialog with custom short codes
 * - Analytics panel: click charts, referrers, device/country breakdown
 * - QR code preview and download
 */
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Chip, Switch, Tooltip, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Tabs, Tab, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
  ToggleButton, ToggleButtonGroup, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode2 as QrCodeIcon,
  BarChart as StatsIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { useLinks, useLinkStats, useLinkClicks, useCreateLink, useUpdateLink, useDeleteLink } from '../hooks/useLinks';
import { useMobile } from '../hooks/useMobile';

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_KEY;
const MAPS_LIBRARIES = ['places'];

const BASE_URL = window.location.origin;
const CHART_COLORS = ['#13B5EA', '#F97316', '#A78BFA', '#4ADE80', '#F472B6', '#FBBF24', '#60A5FA', '#E879F9'];

function getDomain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

// Stats dates arrive as bare 'YYYY-MM-DD' strings; new Date('YYYY-MM-DD')
// parses as UTC midnight and shifts a day back in Pacific time — parse local.
function parseDay(d) {
  return new Date(String(d).slice(0, 10) + 'T00:00:00');
}
export default function LinksPage() {
  const { isMobile } = useMobile();
  const { data: links = [], isLoading } = useLinks();
  const createLink = useCreateLink();
  const updateLink = useUpdateLink();
  const deleteLink = useDeleteLink();

  const [editDialog, setEditDialog] = useState(null);
  const [selectedId, setSelectedId] = useState(null); // link id whose stats are shown
  const [qrDialog, setQrDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copied, setCopied] = useState(null);
  const [mutationError, setMutationError] = useState(null);

  // Toolbar state
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive

  const statsRef = useRef(null);

  // Filter (newest-first natural order from API)
  const filteredLinks = useMemo(() => {
    let list = links;
    if (activeFilter === 'active') list = list.filter(l => l.is_active);
    if (activeFilter === 'inactive') list = list.filter(l => !l.is_active);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(l =>
        (l.title || '').toLowerCase().includes(q) ||
        (l.short_code || '').toLowerCase().includes(q) ||
        (l.destination_url || '').toLowerCase().includes(q) ||
        (l.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [links, activeFilter, search]);

  const totalClicks = useMemo(() => links.reduce((s, l) => s + (l.click_count || 0), 0), [links]);

  const copyUrl = useCallback((code) => {
    navigator.clipboard.writeText(`${BASE_URL}/r/${code}`);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  const clearFilters = () => {
    setSearch('');
    setActiveFilter('all');
  };

  const hasActiveFilters = !!search || activeFilter !== 'all';

  const selectLink = useCallback((id) => {
    setSelectedId(id);
    // Scroll stats panel into view shortly after render
    setTimeout(() => {
      statsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
  }, []);

  const selectedLink = selectedId ? links.find(l => l.id === selectedId) : null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>Links</Typography>
          <Typography variant="body2" color="text.secondary">
            {links.length} link{links.length !== 1 ? 's' : ''} &middot; {totalClicks} total clicks
          </Typography>
        </Box>
        {isMobile ? (
          <IconButton color="primary" onClick={() => setEditDialog({ mode: 'create' })} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
            <AddIcon />
          </IconButton>
        ) : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditDialog({ mode: 'create' })} sx={{ textTransform: 'none' }}>
            New Link
          </Button>
        )}
      </Box>

      {mutationError && (
        <Alert severity="error" onClose={() => setMutationError(null)} sx={{ mb: 2 }}>{mutationError}</Alert>
      )}

      {/* Toolbar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <TextField
          size="small"
          placeholder="Search links…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ flex: '1 1 240px', minWidth: 0, width: { xs: '100%', sm: 'auto' } }}
        />
        <ToggleButtonGroup
          size="small"
          value={activeFilter}
          exclusive
          onChange={(_, v) => v && setActiveFilter(v)}
          sx={{ width: { xs: '100%', sm: 'auto' }, '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'unset' } } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Off</ToggleButton>
        </ToggleButtonGroup>
        {hasActiveFilters && (
          <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
            Clear
          </Button>
        )}
      </Paper>

      {/* List */}
      {links.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <LinkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>No links yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Create your first trackable short link.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditDialog({ mode: 'create' })}>New Link</Button>
        </Paper>
      ) : filteredLinks.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>No links match your search</Typography>
          <Button variant="outlined" onClick={clearFilters} sx={{ mt: 1 }}>Clear filters</Button>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {filteredLinks.map((link) => (
            <Box key={link.id}>
              <LinkMobileCard
                link={link}
                selected={selectedId === link.id}
                copied={copied}
                onSelect={() => selectLink(link.id)}
                onCopy={() => copyUrl(link.short_code)}
                onEdit={() => setEditDialog({ mode: 'edit', link })}
                onDelete={() => setDeleteConfirm(link.id)}
                onShowQR={() => setQrDialog(link)}
                onToggleActive={() => updateLink.mutate({ id: link.id, is_active: !link.is_active })}
              />
              {selectedId === link.id && (
                <Paper variant="outlined" ref={statsRef} sx={{ mt: 1, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                  <StatsPanel
                    linkId={link.id}
                    link={link}
                    onClose={() => setSelectedId(null)}
                  />
                </Paper>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLinks.map((link) => (
                <DesktopLinkRow
                  key={link.id}
                  link={link}
                  selected={selectedId === link.id}
                  copied={copied}
                  onSelect={() => selectLink(link.id)}
                  onCopy={() => copyUrl(link.short_code)}
                  onEdit={() => setEditDialog({ mode: 'edit', link })}
                  onDelete={() => setDeleteConfirm(link.id)}
                  onShowQR={() => setQrDialog(link)}
                  onToggleActive={() => updateLink.mutate({ id: link.id, is_active: !link.is_active })}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Stats panel — desktop renders below the table. Keyed by link so tab/
          days state resets when switching links (a non-GPS link has no Map tab). */}
      {!isMobile && selectedLink && (
        <Paper variant="outlined" ref={statsRef} sx={{ mb: 3 }}>
          <StatsPanel
            key={selectedLink.id}
            linkId={selectedLink.id}
            link={selectedLink}
            onClose={() => setSelectedId(null)}
          />
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      {editDialog && (
        <LinkFormDialog
          mode={editDialog.mode}
          link={editDialog.link}
          onClose={() => setEditDialog(null)}
          onSave={(data) => {
            const onError = (err) => setMutationError(err.message || 'Operation failed');
            if (editDialog.mode === 'create') {
              createLink.mutate(data, { onSuccess: () => setEditDialog(null), onError });
            } else {
              updateLink.mutate({ id: editDialog.link.id, ...data }, { onSuccess: () => setEditDialog(null), onError });
            }
          }}
          saving={createLink.isPending || updateLink.isPending}
        />
      )}

      {/* QR Code Dialog */}
      {qrDialog && (
        <QRDialog link={qrDialog} onClose={() => setQrDialog(null)} />
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Link</DialogTitle>
        <DialogContent>
          <Typography>Are you sure? This will permanently delete this link and all its click data.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              deleteLink.mutate(deleteConfirm, { onSuccess: () => {
                setDeleteConfirm(null);
                if (selectedId === deleteConfirm) setSelectedId(null);
              }});
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Desktop Table Row ───────────────────────────────────────
function DesktopLinkRow({ link, selected, copied, onSelect, onCopy, onEdit, onDelete, onShowQR, onToggleActive }) {
  const hasOgMeta = !!(link.og_title || link.og_description || link.og_image_url);
  const domain = getDomain(link.destination_url);

  return (
    <TableRow
      hover
      selected={selected}
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        opacity: link.is_active ? 1 : 0.55,
        '& > td': { py: 1 },
      }}
    >
      <TableCell sx={{ maxWidth: 280 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
            {link.title || link.short_code}
          </Typography>
          {hasOgMeta && (
            <Tooltip title="Custom social preview">
              <Chip label="OG" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />
            </Tooltip>
          )}
          {link.match_vehicle_location && (
            <Tooltip title="Clicks tagged with vehicle location">
              <Chip icon={<CarIcon sx={{ fontSize: 12 }} />} label="GPS" size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-icon': { ml: '4px', mr: '-2px' } }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }} onClick={(e) => e.stopPropagation()}>
          <Typography variant="caption" color="primary" sx={{ fontFamily: 'monospace' }}>
            /r/{link.short_code}
          </Typography>
          <Tooltip title={copied === link.short_code ? 'Copied!' : 'Copy URL'}>
            <IconButton size="small" onClick={onCopy} sx={{ p: 0.25 }}>
              <CopyIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>

      <TableCell sx={{ maxWidth: 240 }}>
        <Tooltip title={link.destination_url}>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ cursor: 'help' }}>
            {domain || link.destination_url}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell sx={{ maxWidth: 180 }}>
        {link.tags?.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {link.tags.slice(0, 2).map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            ))}
            {link.tags.length > 2 && (
              <Chip label={`+${link.tags.length - 2}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </TableCell>

      <TableCell align="right">
        <Typography variant="body2" fontWeight={600} color={link.click_count > 0 ? 'primary' : 'text.secondary'}>
          {link.click_count || 0}
        </Typography>
      </TableCell>

      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        <Switch size="small" checked={link.is_active} onChange={onToggleActive} />
      </TableCell>

      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="QR code">
          <IconButton size="small" onClick={onShowQR}><QrCodeIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Open destination">
          <IconButton size="small" component="a" href={link.destination_url} target="_blank" rel="noopener noreferrer">
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} color="error"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ── Mobile Card ──────────────────────────────────────────────
function LinkMobileCard({ link, selected, copied, onSelect, onCopy, onEdit, onDelete, onShowQR, onToggleActive }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const hasOgMeta = !!(link.og_title || link.og_description || link.og_image_url);
  const domain = getDomain(link.destination_url);

  return (
    <Paper
      variant="outlined"
      onClick={onSelect}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 1.5 : 1,
        opacity: link.is_active ? 1 : 0.6,
        ...(selected && { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {link.title || link.short_code}
        </Typography>
        {hasOgMeta && (
          <Chip label="OG" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />
        )}
        {link.match_vehicle_location && (
          <Chip icon={<CarIcon sx={{ fontSize: 12 }} />} label="GPS" size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-icon': { ml: '4px', mr: '-2px' } }} />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Typography variant="caption" color="primary" sx={{ fontFamily: 'monospace' }}>
          /r/{link.short_code}
        </Typography>
        <IconButton size="small" onClick={onCopy} sx={{ p: 1 }}>
          <CopyIcon sx={{ fontSize: 16 }} />
          {copied === link.short_code && <Box component="span" sx={{ fontSize: 10, ml: 0.25 }}>✓</Box>}
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        → {domain || link.destination_url}
      </Typography>

      {link.tags?.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {link.tags.slice(0, 4).map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }} onClick={(e) => e.stopPropagation()}>
        <Chip
          icon={<StatsIcon sx={{ fontSize: 14 }} />}
          label={`${link.click_count || 0} clicks`}
          size="small"
          color={link.click_count > 0 ? 'primary' : 'default'}
          onClick={onSelect}
          sx={{ fontWeight: 600, cursor: 'pointer' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Switch size="small" checked={link.is_active} onChange={onToggleActive} />
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { onShowQR(); setMenuAnchor(null); }}>
          <ListItemIcon><QrCodeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>QR Code</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onEdit(); setMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem component="a" href={link.destination_url} target="_blank" rel="noopener noreferrer" onClick={() => setMenuAnchor(null)}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open destination</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { onDelete(); setMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
}

// ── Link Form Dialog ─────────────────────────────────────────
function LinkFormDialog({ mode, link, onClose, onSave, saving }) {
  const { isMobile } = useMobile();
  const [url, setUrl] = useState(link?.destination_url || '');
  const [title, setTitle] = useState(link?.title || '');
  const [shortCode, setShortCode] = useState(link?.short_code || '');
  const [tags, setTags] = useState(link?.tags?.join(', ') || '');
  const [ogTitle, setOgTitle] = useState(link?.og_title || '');
  const [ogDescription, setOgDescription] = useState(link?.og_description || '');
  const [ogImageUrl, setOgImageUrl] = useState(link?.og_image_url || '');
  const [matchVehicleLocation, setMatchVehicleLocation] = useState(link?.match_vehicle_location || false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) return setError('Please upload an image file');
    if (file.size > 10 * 1024 * 1024) return setError('Image must be under 10 MB');
    setUploadingImage(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/upload/link-og', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Upload failed');
      }
      const { url: uploadedUrl } = await res.json();
      setOgImageUrl(uploadedUrl);
    } catch (e) {
      setError(`Upload failed: ${e.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = () => {
    if (!url.trim()) return setError('Destination URL is required');
    let parsed;
    try { parsed = new URL(url); } catch { return setError('Invalid URL format'); }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return setError('Destination must be an http(s) URL');
    }
    if (mode === 'edit' && !shortCode.trim()) {
      return setError('Short code is required — clearing it would break the printed QR code');
    }
    const data = {
      destination_url: url.trim(),
      title: title.trim() || null,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      og_title: ogTitle.trim() || null,
      og_description: ogDescription.trim() || null,
      og_image_url: ogImageUrl.trim() || null,
      match_vehicle_location: matchVehicleLocation,
    };
    if (mode === 'create' && shortCode.trim()) {
      data.short_code = shortCode.trim();
    }
    if (mode === 'edit' && shortCode.trim() !== link.short_code) {
      data.short_code = shortCode.trim();
    }
    setError('');
    onSave(data);
  };

  const hasSocialPreview = !!(ogTitle || ogDescription || ogImageUrl);
  const previewDomain = (() => {
    try { return new URL(url || 'https://portal.rotogears.com').hostname; } catch { return ''; }
  })();

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{mode === 'create' ? 'Create Link' : 'Edit Link'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <TextField
          label="Destination URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/your-page"
          required
          fullWidth
          autoFocus
        />
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="My Campaign Link"
          fullWidth
        />
        <TextField
          label="Short Code"
          value={shortCode}
          onChange={(e) => setShortCode(e.target.value)}
          placeholder={mode === 'create' ? 'auto-generated if blank' : ''}
          helperText={shortCode ? `${BASE_URL}/r/${shortCode}` : mode === 'create' ? 'Leave blank to auto-generate' : 'Required — printed QR codes point at this code'}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start">/r/</InputAdornment>,
          }}
        />
        <TextField
          label="Tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="campaign, social, email"
          helperText="Comma-separated"
          fullWidth
        />

        {/* ── Vehicle location matching ─────────────────────────── */}
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={matchVehicleLocation}
                onChange={(e) => setMatchVehicleLocation(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CarIcon fontSize="small" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>Tag clicks with vehicle location</Typography>
                  <Typography variant="caption" color="text.secondary">
                    For QR codes on the work vehicle — see where each scan happened on a map.
                  </Typography>
                </Box>
              </Box>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        </Box>

        {/* ── Social Preview (Open Graph) ────────────────────────── */}
        <Accordion defaultExpanded={hasSocialPreview} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon fontSize="small" />
              <Typography variant="subtitle2">Social Preview (iMessage, Slack, Facebook…)</Typography>
              {hasSocialPreview && <Chip size="small" label="Custom" color="primary" />}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              When this short link is shared, scrapers will see this preview instead of the destination's own metadata. Leave blank to use the destination's preview.
            </Typography>
            <TextField
              label="Preview Title"
              value={ogTitle}
              onChange={(e) => setOgTitle(e.target.value)}
              placeholder="Headline shown in the preview card"
              fullWidth
              size="small"
            />
            <TextField
              label="Preview Description"
              value={ogDescription}
              onChange={(e) => setOgDescription(e.target.value)}
              placeholder="Short description (1-2 lines)"
              fullWidth
              size="small"
              multiline
              rows={2}
              helperText={`${ogDescription.length}/200 — longer text may be truncated by some platforms`}
            />
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
              />
              {ogImageUrl ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    component="img"
                    src={ogImageUrl}
                    alt="Preview"
                    sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button size="small" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                      {uploadingImage ? 'Uploading…' : 'Replace Image'}
                    </Button>
                    <Button size="small" color="error" onClick={() => setOgImageUrl('')}>Remove</Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={uploadingImage ? <CircularProgress size={16} /> : <UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? 'Uploading…' : 'Upload Preview Image'}
                </Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Recommended: 1200×630px, under 10 MB. Accepts JPG, PNG, WebP, GIF.
              </Typography>
            </Box>

            {/* Live preview card */}
            {hasSocialPreview && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Live preview:</Typography>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', maxWidth: 400, bgcolor: 'background.paper' }}>
                  {ogImageUrl && (
                    <Box
                      component="img"
                      src={ogImageUrl}
                      alt=""
                      sx={{ width: '100%', maxHeight: 210, objectFit: 'cover', display: 'block', bgcolor: 'action.hover' }}
                    />
                  )}
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      {previewDomain}
                    </Typography>
                    {ogTitle && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, mt: 0.25 }}>
                        {ogTitle}
                      </Typography>
                    )}
                    {ogDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.3, mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {ogDescription}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── QR Code Dialog ───────────────────────────────────────────
function QRDialog({ link, onClose }) {
  const canvasRef = useRef(null);
  const url = `${BASE_URL}/r/${link.short_code}`;

  const downloadPNG = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.download = `qr-${link.short_code}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  };

  const downloadSVG = () => {
    const svg = document.getElementById('qr-svg-download');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.download = `qr-${link.short_code}.svg`;
    a.href = URL.createObjectURL(blob);
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        QR Code
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {link.title || link.short_code}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <QRCodeSVG id="qr-svg-download" value={url} size={240} level="M" />
        </Box>
        {/* Hidden canvas for PNG download */}
        <Box ref={canvasRef} sx={{ display: 'none' }}>
          <QRCodeCanvas value={url} size={512} level="M" />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mb: 2 }}>
          {url}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadPNG}
          >
            PNG
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadSVG}
          >
            SVG
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── Stats Panel (Drawer content) ─────────────────────────────
function StatsPanel({ linkId, link, onClose }) {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState(0);
  const { data: stats, isLoading } = useLinkStats(linkId, days);
  const { data: clickData } = useLinkClicks(linkId, { limit: 20 });

  if (!link) return null;

  return (
    <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {link.title || link.short_code}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats?.totalClicks ?? '...'} clicks in last {days} days
          </Typography>
          <Typography variant="caption" color="primary" sx={{ fontFamily: 'monospace' }}>
            /r/{link.short_code}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {[7, 30, 90].map((d) => (
            <Chip
              key={d}
              label={`${d}d`}
              size="small"
              variant={days === d ? 'filled' : 'outlined'}
              color={days === d ? 'primary' : 'default'}
              onClick={() => setDays(d)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : stats ? (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
            <Tab label="Clicks Over Time" />
            <Tab label="Breakdown" />
            <Tab label="Recent Clicks" />
            {link?.match_vehicle_location && <Tab label="Map" icon={<LocationIcon fontSize="small" />} iconPosition="start" />}
          </Tabs>

          {tab === 0 && (
            <Box sx={{ height: 280 }}>
              {stats.clicksByDay.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.clicksByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => parseDay(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      fontSize={11}
                    />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <ChartTooltip
                      labelFormatter={(d) => parseDay(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    />
                    <Bar dataKey="clicks" fill="#13B5EA" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No clicks in this period</Typography>
                </Box>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <PieSection title="Device" data={stats.deviceBreakdown} nameKey="device_type" />
              <PieSection title="Browser" data={stats.browserBreakdown} nameKey="browser" />
              <PieSection title="OS" data={stats.osBreakdown} nameKey="os" />
              <PieSection title="Country" data={stats.countryBreakdown} nameKey="country" />
              {stats.topReferrers.length > 0 && (
                <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Top Referrers</Typography>
                  <Table size="small">
                    <TableBody>
                      {stats.topReferrers.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.referer}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{r.clicks}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

          {tab === 2 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Browser</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Referrer</TableCell>
                    {link?.match_vehicle_location && <TableCell>Vehicle location</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(clickData?.clicks || []).map((click) => (
                    <TableRow key={click.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(click.clicked_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{click.device_type || '—'}</TableCell>
                      <TableCell>{click.browser || '—'}</TableCell>
                      <TableCell>{click.country || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {click.referer || 'Direct'}
                      </TableCell>
                      {link?.match_vehicle_location && (
                        <TableCell sx={{ maxWidth: 240 }}>
                          <VehicleLocationCell click={click} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {(!clickData?.clicks || clickData.clicks.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={link?.match_vehicle_location ? 6 : 5} align="center">
                        <Typography variant="body2" color="text.secondary">No clicks recorded yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && link?.match_vehicle_location && (
            <ClickMap linkId={linkId} days={days} />
          )}
        </>
      ) : null}
    </Box>
  );
}

// ── Click Map (vehicle GPS per click) ────────────────────────
function ClickMap({ linkId, days }) {
  // Filter the window server-side; a client-side cutoff over a capped page
  // would silently drop older geo-tagged scans on busy links.
  const from = useMemo(() => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(), [days]);
  const { data: clickData, isLoading } = useLinkClicks(linkId, { limit: 1000, from });
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'rfos-google-maps',
    googleMapsApiKey: MAPS_API_KEY || '',
    libraries: MAPS_LIBRARIES,
  });
  const mapRef = useRef(null);
  const centerRef = useRef(null); // initial center only — never churned, so pans aren't wiped
  const [selected, setSelected] = useState(null);

  const points = useMemo(
    () => (clickData?.clicks || []).filter((c) => c.vehicle_lat != null && c.vehicle_lng != null),
    [clickData]
  );

  if (centerRef.current === null && points.length > 0) {
    centerRef.current = { lat: parseFloat(points[0].vehicle_lat), lng: parseFloat(points[0].vehicle_lng) };
  }

  const fitToPoints = useCallback((map, pts) => {
    if (!map || pts.length === 0) return;
    if (pts.length === 1) {
      map.setCenter({ lat: parseFloat(pts[0].vehicle_lat), lng: parseFloat(pts[0].vehicle_lng) });
      map.setZoom(15); // fitBounds on a single point zooms to rooftop level
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pts.forEach((p) => { bounds.extend({ lat: parseFloat(p.vehicle_lat), lng: parseFloat(p.vehicle_lng) }); });
    map.fitBounds(bounds, 64);
  }, []);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    fitToPoints(map, points);
  };

  // Refit only when the set of pins actually changes (days switch, new scan) —
  // identity-only refetches leave the user's pan/zoom alone.
  const lastPointsKeyRef = useRef('');
  useEffect(() => {
    const key = points.map((p) => p.id).join('|');
    if (key === lastPointsKeyRef.current) return;
    lastPointsKeyRef.current = key;
    if (mapRef.current) fitToPoints(mapRef.current, points);
  }, [points, fitToPoints]);

  if (!MAPS_API_KEY) {
    return <Alert severity="error">Google Maps API key missing (VITE_GOOGLE_PLACES_KEY).</Alert>;
  }
  if (loadError) {
    return <Alert severity="error">Failed to load Google Maps: {loadError.message}</Alert>;
  }
  if (isLoading || !isLoaded) {
    return (
      <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (points.length === 0) {
    return (
      <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No geo-tagged clicks in this period yet</Typography>
      </Box>
    );
  }

  const pinColor = (kind) => {
    if (kind === 'live_driving') return '#13B5EA';
    if (kind === 'live_parked') return '#4ADE80';
    return '#F97316';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          {points.length} geo-tagged scan{points.length === 1 ? '' : 's'} in last {days}d
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" label="Driving" sx={{ bgcolor: '#13B5EA', color: 'white', height: 18, fontSize: '0.65rem' }} />
        <Chip size="small" label="Parked" sx={{ bgcolor: '#4ADE80', color: 'white', height: 18, fontSize: '0.65rem' }} />
        <Chip size="small" label="Last known" sx={{ bgcolor: '#F97316', color: 'white', height: 18, fontSize: '0.65rem' }} />
      </Box>
      <Box sx={{ height: 480, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={centerRef.current || { lat: 34.4208, lng: -119.6982 }}
          zoom={12}
          onLoad={handleMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            clickableIcons: false,
          }}
        >
          {points.map((p) => {
            const lat = parseFloat(p.vehicle_lat);
            const lng = parseFloat(p.vehicle_lng);
            return (
              <Marker
                key={p.id}
                position={{ lat, lng }}
                onClick={() => setSelected(p)}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: pinColor(p.vehicle_match_kind),
                  fillOpacity: 0.85,
                  strokeColor: '#fff',
                  strokeWeight: 2,
                }}
              />
            );
          })}
          {selected && (
            <InfoWindow
              position={{ lat: parseFloat(selected.vehicle_lat), lng: parseFloat(selected.vehicle_lng) }}
              onCloseClick={() => setSelected(null)}
            >
              <Box sx={{ minWidth: 200, p: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {new Date(selected.clicked_at).toLocaleString()}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                  {selected.vehicle_location_label || `${selected.vehicle_lat}, ${selected.vehicle_lng}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip size="small" label={(selected.vehicle_match_kind || '').replace('_', ' ')} sx={{ height: 18, fontSize: '0.65rem' }} />
                  {selected.device_type && <Chip size="small" label={selected.device_type} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                  {selected.country && <Chip size="small" label={selected.country} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                </Box>
                <Box
                  component="a"
                  href={`https://www.google.com/maps?q=${selected.vehicle_lat},${selected.vehicle_lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, mt: 0.75, fontSize: '0.7rem' }}
                >
                  Open in Google Maps <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      </Box>
    </Box>
  );
}

// ── Pie Chart Section ────────────────────────────────────────
function PieSection({ title, data, nameKey }) {
  if (!data || data.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Box sx={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="clicks"
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              outerRadius={70}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
              fontSize={11}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <ChartTooltip />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}


// ── Vehicle Location Cell (Recent Clicks) ────────────────────
function VehicleLocationCell({ click }) {
  const { isMobile } = useMobile();
  const { vehicle_lat, vehicle_lng, vehicle_location_label, vehicle_match_kind, vehicle_lag_seconds } = click;

  // Distinguish the three empty states: never attempted (null), Tessie had
  // nothing at scan time (no_data), enrichment attempted but lost (pending).
  if (vehicle_match_kind === 'no_data') {
    return (
      <Tooltip title="No vehicle data available at scan time" arrow>
        <Typography variant="caption" color="text.disabled">no data</Typography>
      </Tooltip>
    );
  }
  if (vehicle_match_kind === 'pending') {
    if (isMobile) {
      return <Typography variant="caption" color="text.disabled">lookup pending</Typography>;
    }
    return (
      <Tooltip title="Location lookup didn't complete for this scan" arrow>
        <Typography variant="caption" color="text.disabled">…</Typography>
      </Tooltip>
    );
  }
  if (!vehicle_lat && !vehicle_lng && !vehicle_location_label) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }

  const hasCoords = vehicle_lat != null && vehicle_lng != null;
  const mapsUrl = hasCoords ? `https://www.google.com/maps?q=${vehicle_lat},${vehicle_lng}` : null;

  let icon, kindLabel;
  if (vehicle_match_kind === 'live_driving') { icon = <CarIcon sx={{ fontSize: 16 }} color="primary" />; kindLabel = 'Driving'; }
  else if (vehicle_match_kind === 'live_parked') { icon = <LocationIcon sx={{ fontSize: 16 }} color="success" />; kindLabel = 'Parked'; }
  else { icon = <LocationIcon sx={{ fontSize: 16 }} color="warning" />; kindLabel = 'Last known'; }

  const lagText = vehicle_lag_seconds != null
    ? (vehicle_lag_seconds < 60 ? `±${vehicle_lag_seconds}s` : `±${Math.round(vehicle_lag_seconds / 60)}m`)
    : null;

  const tooltipParts = [kindLabel];
  if (lagText) tooltipParts.push(`live data ${lagText} from click time`);
  else if (vehicle_match_kind === 'fallback') tooltipParts.push('live data unavailable — using last drive end');

  return (
    <Tooltip title={tooltipParts.join(' • ')} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {vehicle_location_label || (hasCoords ? `${vehicle_lat.toFixed?.(4) ?? vehicle_lat}, ${vehicle_lng.toFixed?.(4) ?? vehicle_lng}` : '—')}
        </Typography>
        {mapsUrl && (
          <Tooltip title="Open in Google Maps">
            <IconButton
              size="small"
              component="a"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ p: 0.25 }}
            >
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Tooltip>
  );
}
